import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Target, Plus, Edit2, Trash2, AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import BudgetDialog from "@/components/BudgetDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIOD_LABEL = { weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" };
const ALERT_RANK = { ok: 0, warning: 1, critical: 2, exceeded: 3 };

function BudgetGoals({ currency, categories, refreshTrigger }) {
  const [budgets, setBudgets] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const prevAlerts = useRef({});
  const hasRequestedPerm = useRef(false);

  const notifyThresholdCrossed = useCallback((budget, categoryName) => {
    const title = `Budget alert: ${categoryName}`;
    const body = budget.alert_message;

    // In-app toast banner
    if (budget.alert_level === 'exceeded' || budget.alert_level === 'critical') {
      toast.error(`${title} — ${body}`, { duration: 6000 });
    } else {
      toast.warning(`${title} — ${body}`, { duration: 5000 });
    }

    // Browser native notification (if permission granted)
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, { body, tag: `budget-${budget.budget_id}` });
      } catch {
        // Some browsers block constructor on insecure contexts - silent fail
      }
    }
  }, []);

  const fetchBudgets = useCallback(async () => {
    try {
      const response = await fetch(`${API}/budgets?currency=${currency}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBudgets(data);

        // Detect threshold escalations vs previous state
        const categoryMap = Object.fromEntries(categories.map(c => [c.category_id, c.name]));
        data.forEach((b) => {
          const prevLevel = prevAlerts.current[b.budget_id] ?? 'ok';
          const currLevel = b.alert_level;
          if ((ALERT_RANK[currLevel] ?? 0) > (ALERT_RANK[prevLevel] ?? 0)) {
            notifyThresholdCrossed(b, categoryMap[b.category] || b.category);
          }
          prevAlerts.current[b.budget_id] = currLevel;
        });
      }
    } catch {
      // Silent fail - will retry on next render
    }
  }, [currency, categories, notifyThresholdCrossed]);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets, refreshTrigger]);

  // Request browser notification permission once on first render
  useEffect(() => {
    if (hasRequestedPerm.current) return;
    hasRequestedPerm.current = true;
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Reset alert-tracking when currency changes so re-loaded list doesn't re-fire stale alerts
  useEffect(() => {
    prevAlerts.current = {};
  }, [currency]);

  const handleAdd = () => {
    setEditingBudget(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setIsDialogOpen(true);
  };

  const handleDelete = async (budgetId) => {
    try {
      const response = await fetch(`${API}/budgets/${budgetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Budget deleted');
        fetchBudgets();
      }
    } catch {
      toast.error('Failed to delete budget');
    }
  };

  const handleSaved = () => {
    fetchBudgets();
    setIsDialogOpen(false);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.category_id === categoryId);
    return category ? category.name : categoryId;
  };

  const getAlertStyle = (level) => {
    switch (level) {
      case 'exceeded':
        return { bar: 'bg-destructive', text: 'text-destructive', icon: AlertCircle, badge: 'bg-destructive/10 text-destructive border-destructive/20' };
      case 'critical':
        return { bar: 'bg-destructive', text: 'text-destructive', icon: AlertCircle, badge: 'bg-destructive/10 text-destructive border-destructive/20' };
      case 'warning':
        return { bar: 'bg-chart-4', text: 'text-chart-4', icon: AlertTriangle, badge: 'bg-chart-4/10 border-chart-4/20' };
      default:
        return { bar: 'bg-success', text: 'text-success', icon: CheckCircle2, badge: 'bg-success/10 text-success border-success/20' };
    }
  };

  return (
    <Card data-testid="budget-goals-card" className="mb-8 border border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold flex items-center gap-2">
              <Target className="h-5 w-5" strokeWidth={1.5} />
              Budget Goals
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-1">
              Set monthly spending limits and get smart alerts
            </CardDescription>
          </div>
          <Button
            data-testid="add-budget-button"
            onClick={handleAdd}
            className="transition-transform duration-200 hover:-translate-y-px"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Budget
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        {budgets.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No budget goals yet. Set one to track your spending limits in {currency}.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {budgets.map((budget) => {
              const style = getAlertStyle(budget.alert_level);
              const Icon = style.icon;
              const widthPercent = Math.min(budget.percent_used, 100);
              return (
                <div
                  key={budget.budget_id}
                  data-testid={`budget-${budget.budget_id}`}
                  className="p-4 border border-border rounded-md transition-colors duration-200 hover:bg-slate-50"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-semibold text-sm">{getCategoryName(budget.category)}</p>
                        <Badge variant="outline" className="text-xs font-normal">
                          {PERIOD_LABEL[budget.period] || 'Monthly'}
                        </Badge>
                        <Badge className={style.badge}>
                          {budget.percent_used.toFixed(0)}%
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {currency} {budget.spent.toFixed(2)} of {currency} {budget.limit_amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        data-testid={`edit-budget-${budget.budget_id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(budget)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            data-testid={`delete-budget-${budget.budget_id}`}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Budget Goal</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this budget? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(budget.budget_id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {/* Progress bar with alert color */}
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full ${style.bar} transition-all duration-300`}
                      style={{ width: `${widthPercent}%` }}
                    />
                  </div>

                  {/* Alert message */}
                  <div className={`flex items-start gap-2 mt-2 ${style.text}`}>
                    <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-xs leading-relaxed">{budget.alert_message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <BudgetDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        budget={editingBudget}
        categories={categories}
        currency={currency}
        onSaved={handleSaved}
      />
    </Card>
  );
}

export default BudgetGoals;
